import { BaseTheme, Grid, styled, Typography } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { format_percentage_value, formatCurrencyWithSymbol } from 'util/misc'

export type PriceDetailProps = {
  from?: boolean
  discountSavingPerItem?: number
  pricePerItem?: number
  outputPower?: number
  currencySymbol: string
  quantityPerItem?: number
  isShop3?: boolean
  pricePerWatt?: string | null
  displayDistributorName?: string
}

export const formatPricePerWattWithUnit = (value: number, currencySymbol: string, orgIso2?: string) => {
  if (value < 1) {
    // TODO: fix me, makes it more robust
    const unit = orgIso2 === 'GB' ? 'p' : 'c'
    return `${format_percentage_value(value, 2)}${unit}`
  } else {
    return `${formatCurrencyWithSymbol(value, currencySymbol)}`
  }
}

export const getPricePerWatt = ({
  power,
  price,
  quantityPerItem,
}: {
  power?: number
  price?: number
  quantityPerItem?: number
}) => {
  if (!power || price == null) {
    return
  }
  return price / ((quantityPerItem || 1) * power)
}

const PriceDetail = ({
  from,
  pricePerItem,
  outputPower,
  quantityPerItem,
  discountSavingPerItem,
  currencySymbol,
  isShop3 = true,
  pricePerWatt,
  displayDistributorName,
}: PriceDetailProps) => {
  const translate = useTranslate()

  if (pricePerItem == null) {
    return null
  }
  let originalPrice = pricePerItem
  let priceAfterDiscount = pricePerItem - (discountSavingPerItem || 0)

  if (isShop3 === true) {
    originalPrice = discountSavingPerItem ? pricePerItem / (1 - discountSavingPerItem) : pricePerItem
    priceAfterDiscount = pricePerItem
  }
  const hasDiscount = !!discountSavingPerItem

  return (
    <PriceDetailLayout
      PricePerUnit={
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <Typography
              variant="h5"
              {...(hasDiscount && !from ? { style: { color: BaseTheme.palette.red[800] } } : {})}
            >
              {from && `${translate('From')} `}
              {formatCurrencyWithSymbol(hasDiscount ? priceAfterDiscount : originalPrice, currencySymbol)}
            </Typography>
          </Grid>
          {hasDiscount && !from && (
            <Grid item>
              <Typography variant="body1" style={{ textDecoration: 'line-through' }}>
                {formatCurrencyWithSymbol(originalPrice, currencySymbol)}
              </Typography>
            </Grid>
          )}
        </Grid>
      }
      PricePerWatt={
        <Typography variant={displayDistributorName ? 'body1' : 'h6'}>
          {from && `${translate('From')} `}
          {pricePerWatt ?? '-'}
          {displayDistributorName && ` (${displayDistributorName})`}
        </Typography>
      }
    />
  )
}

const PriceContainer = styled(Grid)({
  flexDirection: 'column',
  gap: 16,
})

const PriceLabel = styled(Typography)({
  fontWeight: 475,
})

export const PriceDetailLayout = ({
  PricePerUnit,
  PricePerWatt,
}: {
  PricePerUnit: React.ReactNode
  PricePerWatt?: React.ReactNode
}) => {
  const translate = useTranslate()
  return (
    <PriceContainer container>
      <Grid container item spacing={1} alignItems="center">
        <Grid item>
          <PriceLabel variant="body1">{translate('Price per unit')}:</PriceLabel>
        </Grid>
        <Grid item>{PricePerUnit}</Grid>
      </Grid>
      {PricePerWatt && (
        <Grid container item spacing={1} alignItems="center">
          <Grid item>
            <PriceLabel variant="body1">{translate('Price per watt')}:</PriceLabel>
          </Grid>
          <Grid item>{PricePerWatt}</Grid>
        </Grid>
      )}
    </PriceContainer>
  )
}

export default PriceDetail
